import React, { useMemo, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import { Card } from '../../components/shared/DataToolbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { LoadingState, ErrorState } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import * as api from '../../api/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

function CrudList({ listFn, createFn, updateFn, deleteFn, title, extraFields }) {
  const { data, loading, error, refetch } = useApi(listFn, [listFn]);
  const rows = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  const [dialog, setDialog] = useState({ open: false, editing: null, form: { name: '', active: true } });

  const openNew = () => setDialog({ open: true, editing: null, form: { name: '', active: true } });
  const openEdit = (it) => setDialog({ open: true, editing: it.id, form: { ...it } });
  const submit = async () => {
    try {
      if (dialog.editing) await updateFn(dialog.editing, dialog.form);
      else await createFn(dialog.form);
      toast.success('Saved'); setDialog({ open: false, editing: null, form: { name: '', active: true } }); refetch();
    } catch (e) { toast.error(e?.message); }
  };
  const del = async (it) => { try { await deleteFn(it.id); toast.success('Deleted'); refetch(); } catch (e) { toast.error(e?.message); } };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;
  return (
    <>
      <div className="flex justify-end mb-3"><Button onClick={openNew} className="bg-teal-700 hover:bg-teal-800 h-9"><Plus className="h-4 w-4 mr-1" /> Add {title.toLowerCase()}</Button></div>
      <Card className="divide-y divide-slate-100">
        {rows.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No entries yet.</div>}
        {rows.map(it => (
          <div key={it.id} className="p-4 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900">{it.name || it.label}</div>
              {it.description && <div className="text-xs text-slate-500">{it.description}</div>}
            </div>
            <span className="text-xs text-slate-500">Active</span>
            <Switch checked={!!it.active} onCheckedChange={(v) => updateFn(it.id, { ...it, active: v }).then(refetch).catch((e) => toast.error(e?.message))} />
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(it)}><Edit2 className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => del(it)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </Card>
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog(d => ({ ...d, open: o }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dialog.editing ? `Edit ${title}` : `Add ${title}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={dialog.form.name || ''} onChange={(e) => setDialog(d => ({ ...d, form: { ...d.form, name: e.target.value } }))} className="mt-1.5" /></div>
            {extraFields && extraFields(dialog.form, (v) => setDialog(d => ({ ...d, form: { ...d.form, ...v } })))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(d => ({ ...d, open: false }))}>Cancel</Button><Button onClick={submit} disabled={!dialog.form.name && !dialog.form.label} className="bg-teal-700 hover:bg-teal-800">{dialog.editing ? 'Save' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PolicyOptionsCrud() {
  const { data: typesData } = useApi(api.listPolicyTypes, []);
  const types = useMemo(() => Array.isArray(typesData) ? typesData : (typesData?.items || []), [typesData]);
  const listFn = React.useCallback(() => api.listPolicyOptions(), []);
  return (
    <CrudList
      listFn={listFn}
      createFn={api.createPolicyOption}
      updateFn={api.updatePolicyOption}
      deleteFn={api.deletePolicyOption}
      title="Policy option"
      extraFields={(form, patch) => (
        <>
          <div><Label>Policy type</Label>
            <Select value={form.policy_type_id ? String(form.policy_type_id) : ''} onValueChange={(v) => patch({ policy_type_id: Number(v) || v })}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{types.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Label</Label><Input value={form.label || ''} onChange={(e) => patch({ label: e.target.value })} className="mt-1.5" /></div>
        </>
      )}
    />
  );
}

export default function AdminMasterData() {
  return (
    <div>
      <PageHeader title="Master data" description="Global features, house rules and policy definitions used across all listings." />
      <Tabs defaultValue="features">
        <TabsList className="bg-white border border-slate-200 rounded-lg p-1 flex-wrap h-auto">
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="rules">House rules</TabsTrigger>
          <TabsTrigger value="policyTypes">Policy types</TabsTrigger>
          <TabsTrigger value="policyOptions">Policy options</TabsTrigger>
        </TabsList>
        <TabsContent value="features" className="mt-4">
          <CrudList listFn={api.listFeatures} createFn={api.createFeature} updateFn={api.updateFeature} deleteFn={api.deleteFeature} title="Feature" extraFields={(f, p) => (<div><Label>Icon (lucide name)</Label><Input value={f.icon || ''} onChange={(e) => p({ icon: e.target.value })} className="mt-1.5" /></div>)} />
        </TabsContent>
        <TabsContent value="rules" className="mt-4">
          <CrudList listFn={api.listRules} createFn={api.createRule} updateFn={api.updateRule} deleteFn={api.deleteRule} title="Rule" />
        </TabsContent>
        <TabsContent value="policyTypes" className="mt-4">
          <CrudList listFn={api.listPolicyTypes} createFn={api.createPolicyType} updateFn={api.updatePolicyType} deleteFn={api.deletePolicyType} title="Policy type" extraFields={(f, p) => (<div><Label>Description</Label><Input value={f.description || ''} onChange={(e) => p({ description: e.target.value })} className="mt-1.5" /></div>)} />
        </TabsContent>
        <TabsContent value="policyOptions" className="mt-4">
          <PolicyOptionsCrud />
        </TabsContent>
      </Tabs>
    </div>
  );
}
